/**
 * Lógica pura do ranking de representante.
 *
 * Vive fora do `.tsx` pela mesma razão do `feed.ts`: o `node --test` remove
 * tipos de `.ts` mas não compila JSX, então o que é testável mora aqui e o
 * componente só desenha. Daí também a ausência de import de runtime —
 * `import type` é apagado, e qualquer outro exigiria extensão explícita que o
 * `tsc` recusa.
 */

import type { Representante } from './api'

/** O pódio. Da 4ª posição em diante a tela mostra só o número. */
export const MEDALHA: Record<number, { emoji: string; rotulo: string }> = {
  1: { emoji: '🥇', rotulo: 'primeiro lugar' },
  2: { emoji: '🥈', rotulo: 'segundo lugar' },
  3: { emoji: '🥉', rotulo: 'terceiro lugar' },
}

/** O que a tela mostra quando o cadastro do ERP não tem aquele nome. */
export const SEM_NOME = '—'

/**
 * Classe da linha. O pódio ganha classe própria (`rank-1`..`rank-3`) para o CSS
 * dar destaque às três primeiras sem a tela precisar saber o que é destaque.
 */
export function classeLinhaRanking(posicao: number): string {
  const base = 'linha-ranking'
  return posicao >= 1 && posicao <= 3 ? `${base} rank-${posicao} podio` : base
}

/**
 * Resolve as três colunas de nome.
 *
 * O cadastro do ERP tem buracos reais, medidos em 13/10: o `DIRETO DE FABRICA`
 * (1º lugar da Alakazoo e da Malhas) não tem fantasia nem pessoa; outros têm
 * fantasia igual à razão social; e representante fora de `PEDI_020` chega com
 * os três nulos — e a linha VALE, porque carrega valor que está no total da
 * divisão.
 *
 * Três decisões, para a tela não mentir nem repetir:
 *
 *  - a coluna PRINCIPAL cai para a fantasia e depois para o nome pessoal
 *    quando a razão social falta. Linha sem nome nenhum no telão é um pedido
 *    de explicação na sala, e a resposta ("representante fora do cadastro") não
 *    cabe lá;
 *  - nome REPETIDO sai uma vez só: fantasia idêntica à razão social vira `—`,
 *    porque três colunas com o mesmo texto parecem defeito de carregamento;
 *  - a comparação ignora caixa e espaço de sobra, que é como a duplicata
 *    aparece de verdade no cadastro.
 */
export function nomesDoRepresentante(representante: Representante): {
  principal: string
  fantasia: string
  pessoal: string
} {
  const nome = texto(representante.nome)
  const fantasia = texto(representante.fantasia)
  const pessoal = texto(representante.nomePessoal)

  const principal = nome ?? fantasia ?? pessoal
  if (principal === null) {
    return { principal: SEM_REPRESENTANTE, fantasia: SEM_NOME, pessoal: SEM_NOME }
  }

  return {
    principal,
    fantasia: mesmoNome(fantasia, principal) ? SEM_NOME : fantasia ?? SEM_NOME,
    pessoal:
      mesmoNome(pessoal, principal) || mesmoNome(pessoal, fantasia)
        ? SEM_NOME
        : pessoal ?? SEM_NOME,
  }
}

/** Quem não está no cadastro de representante do ERP. Dito, não escondido. */
export const SEM_REPRESENTANTE = 'sem representante'

function texto(valor: string | null): string | null {
  if (typeof valor !== 'string') return null
  const limpo = valor.trim()
  return limpo === '' ? null : limpo
}

function mesmoNome(um: string | null, outro: string | null): boolean {
  if (um === null || outro === null) return false
  return um.trim().toLowerCase() === outro.trim().toLowerCase()
}

/**
 * O ranking mudou de conteúdo?
 *
 * Usado para decidir se vale trocar o array no estado da tela: array novo com o
 * mesmo conteúdo invalidaria o `memo` das 15 linhas sem nada ter mudado.
 */
export function rankingMudou(novos: Representante[], velhos: Representante[]): boolean {
  if (novos.length !== velhos.length) return true
  return novos.some((novo, indice) => {
    const velho = velhos[indice]
    /* Tamanhos iguais já foram comparados, mas o acesso por índice é tipado
       como possivelmente ausente — e tratar isso como "mudou" é o lado seguro:
       no pior caso a tela repinta uma vez. */
    if (velho === undefined) return true
    return novo.codigo !== velho.codigo
      || novo.posicao !== velho.posicao
      || novo.valor !== velho.valor
      || novo.nome !== velho.nome
  })
}
